import React from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, Timer } from "@phosphor-icons/react";
import useButtonConfig from "Hooks/useButtonConfig";
import Container from "Components/Container/Container";

/**
 * HeroActionCard — mobile-only section under the Hero.
 * Mirrors the Dalilouki mobile app's "actionCard" (HomeSlider.jsx):
 * dark #1f1f22 bg, subtle white border, outlined step circles with connectors,
 * full-width primary CTA with a red glow shadow, and an optional subtitle row.
 * Hidden on lg+ because the desktop Hero has its own inline steps/CTA.
 */
const HeroActionCard = () => {
  const { t, i18n } = useTranslation();
  const buttonConfig = useButtonConfig();
  const isRTL = i18n.language === "ar";

  const steps = [
    { num: "1", label: t("hero.step_1") },
    { num: "2", label: t("hero.step_2") },
    { num: "3", label: t("hero.step_3") },
  ];

  return (
    <section className="lg:hidden">
      <Container>
        <div className="bg-[#1f1f22] border border-white/[0.14] rounded-[20px] p-6 flex flex-col items-center">
          {/* Steps row with connectors */}
          <div className="flex items-start justify-center gap-2 mb-6 w-full">
            {steps.map((step, i) => (
              <React.Fragment key={i}>
                <div className="flex flex-col items-center gap-2 flex-1 max-w-[90px]">
                  <div className="w-8 h-8 rounded-full border-[1.5px] border-white/[0.35] flex items-center justify-center">
                    <span className="text-white text-sm font-bold">{step.num}</span>
                  </div>
                  <span className="text-white/75 text-xs font-medium text-center leading-tight">
                    {step.label}
                  </span>
                </div>
                {i < steps.length - 1 && (
                  <div className="w-5 h-px bg-white/[0.18] mt-4 flex-shrink-0" />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* CTA */}
          <button
            onClick={buttonConfig.action}
            className="w-full min-h-[54px] flex items-center justify-center gap-2.5 bg-primary hover:bg-darkPrimary text-white text-base font-bold py-4 px-7 rounded-[14px] transition-colors duration-300 shadow-[0_6px_12px_rgba(237,26,77,0.35)]"
          >
            <span className="tracking-wide">{buttonConfig.text}</span>
            <ArrowRight size={18} className={isRTL ? "rotate-180" : ""} />
          </button>

          {buttonConfig.subtitle && (
            <div className="mt-3.5 flex items-center gap-1.5 text-xs text-lightWhite">
              <Timer size={13} />
              <span>{buttonConfig.subtitle}</span>
            </div>
          )}
        </div>
      </Container>
    </section>
  );
};

export default HeroActionCard;
